// src/pages/clientsPage.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Tag, Space, Input, Select, message, Tabs, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, UserOutlined, EditOutlined, WalletOutlined, ContainerOutlined, UserAddOutlined } from '@ant-design/icons';
import { getAllClients, searchClientsByName } from '../services/clientService';
import ClientCreationModal from '../components/clients/ClientCreationModal';
import ClientEditModal from '../components/clients/ClientEditModal';
import AgregarClienteRunningAccountModal from '../components/clients/AgregarClienteRunningAccountModal';
import CuentasCorrientesTab from '../components/clients/CuentasCorrientesTab';
import CuentasAbiertasTab from '../components/clients/CuentasAbiertasTab';
import './clientsPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;

const ClientsPage = () => {
    const location = useLocation();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Get initial tab from URL query param
    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') || 'clientes';
    };
    const [activeTab, setActiveTab] = useState(getInitialTab);
    
    // Update active tab when URL changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get('tab') || 'clientes';
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [location.search]);
    
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // Filters
    const [filters, setFilters] = useState({
        searchTerm: '',
        condicionImpositiva: null,
    });

    // Modal state
    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [runningAccountModalVisible, setRunningAccountModalVisible] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // Fetch clients from backend
    const fetchClients = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            // If there's a search term, use search endpoint
            if (filters.searchTerm && filters.searchTerm.trim().length > 0) {
                const response = await searchClientsByName(filters.searchTerm, 500);
                
                if (response.results) {
                    let filteredResults = response.results;
                    
                    // Apply additional filters
                    if (filters.condicionImpositiva) {
                        filteredResults = filteredResults.filter(
                            client => client.condicion_impositiva === filters.condicionImpositiva
                        );
                    }
                    
                    setClients(filteredResults);
                    setPagination({
                        current: 1,
                        pageSize: pageSize,
                        total: filteredResults.length,
                    });
                } else {
                    setClients([]);
                    setPagination({ current: 1, pageSize: pageSize, total: 0 });
                }
            } else {
                // Use getAllClients endpoint with pagination and filters
                const apiFilters = {};
                if (filters.condicionImpositiva) {
                    apiFilters.condicionImpositiva = filters.condicionImpositiva;
                }
                
                const response = await getAllClients(page, pageSize, apiFilters);
                
                if (response && response.success) {
                    setClients(response.clients);
                    setPagination({
                        current: response.pagination.current_page,
                        pageSize: response.pagination.page_size,
                        total: response.pagination.total_count,
                    });
                } else {
                    message.error('Error al cargar clientes');
                    setClients([]);
                }
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            message.error('Error al cargar clientes del servidor');
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        fetchClients(paginationConfig.current, paginationConfig.pageSize);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        fetchClients(1, pagination.pageSize);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            searchTerm: '',
            condicionImpositiva: null,
        });
        // Fetch without filters
        setTimeout(() => fetchClients(1, pagination.pageSize), 100);
    };

    // Handle client creation
    const handleClientCreated = (newClient) => {
        message.success(`Cliente "${newClient.razon_social}" creado exitosamente`);
        fetchClients(pagination.current, pagination.pageSize);
    };

    // Handle edit button click
    const handleEditClick = (client) => {
        setSelectedClient(client);
        setEditModalVisible(true);
    };

    // Handle client update
    const handleClientUpdated = (updatedClient) => {
        message.success(`Cliente "${updatedClient.razon_social}" actualizado exitosamente`);
        fetchClients(pagination.current, pagination.pageSize);
    };

    // Handle running account enrollment button click
    const handleRunningAccountClick = (client) => {
        setSelectedClient(client);
        setRunningAccountModalVisible(true);
    };

    // Handle client added to running account
    const handleClientAddedToRunningAccount = (newClient) => {
        message.success(`Cliente "${newClient.nombre || newClient.razon_social}" agregado a Cuentas Abiertas exitosamente`);
        setSelectedClient(null);
    };

    // Get tax status color
    const getTaxStatusColor = (status) => {
        const statusColors = {
            'RESPONSABLE INSCRIPTO': 'blue',
            'MONOTRIBUTO': 'green',
            'EXENTO': 'orange',
            'CONSUMIDOR FINAL': 'purple',
        };
        return statusColors[status] || 'default';
    };

    // Get status color
    const getStatusColor = (status) => {
        return status === 'ACTIVO' ? 'success' : 'default';
    };

    // Format CUIT with hyphens
    const formatCuit = (cuit) => {
        if (!cuit) return 'N/A';
        const cleaned = cuit.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
        }
        return cuit;
    };

    // Table columns
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'CUIT',
            dataIndex: 'documento_nro',
            key: 'documento_nro',
            width: 150,
            render: (cuit) => formatCuit(cuit),
        },
        {
            title: 'Razón Social',
            dataIndex: 'razon_social',
            key: 'razon_social',
            ellipsis: true,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Condición Impositiva',
            dataIndex: 'condicion_impositiva',
            key: 'condicion_impositiva',
            width: 200,
            render: (status) => (
                <Tag color={getTaxStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Localidad',
            dataIndex: 'localidad',
            key: 'localidad',
            width: 150,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Provincia',
            dataIndex: 'provincia',
            key: 'provincia',
            width: 150,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Teléfono',
            dataIndex: 'telefono',
            key: 'telefono',
            width: 150,
            render: (text) => text || '-',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            key: 'estado',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Editar cliente">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Agregar a Cuenta Abierta">
                        <Button
                            type="default"
                            size="small"
                            icon={<UserAddOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Habilitación de Cuenta Abierta — permite registrar ventas en cuenta corriente para este cliente'); return; }
                                handleRunningAccountClick(record);
                            }}
                            style={{ color: '#722ed1', borderColor: '#722ed1' }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="clients-page-container">
            <Tabs 
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                type="card"
                className="clients-tabs"
                items={[
                    {
                        key: 'clientes',
                        label: (
                            <span>
                                <UserOutlined style={{ marginRight: 8 }} />
                                Clientes
                            </span>
                        ),
                        children: (
                            <>
                                <div className="clients-actions">
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => setModalVisible(true)}
                                        >
                                            Nuevo Cliente
                                        </Button>
                                        <Button
                                            type="default"
                                            icon={<ReloadOutlined />}
                                            onClick={() => fetchClients(pagination.current, pagination.pageSize)}
                                        >
                                            Actualizar
                                        </Button>
                                    </Space>
                                </div>

                                {/* Filters */}
                                <div className="clients-filters">
                                    <Space wrap size="middle">
                                        <Input
                                            placeholder="Buscar por razón social..."
                                            style={{ width: 300 }}
                                            prefix={<SearchOutlined />}
                                            value={filters.searchTerm}
                                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                                            onPressEnter={handleApplyFilters}
                                            allowClear
                                        />

                                        <Select
                                            placeholder="Condición Impositiva"
                                            style={{ width: 220 }}
                                            allowClear
                                            value={filters.condicionImpositiva}
                                            onChange={(value) => handleFilterChange('condicionImpositiva', value)}
                                        >
                                            <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                                            <Option value="MONOTRIBUTO">Monotributo</Option>
                                            <Option value="EXENTO">Exento</Option>
                                            <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                                        </Select>

                                        <Button type="primary" onClick={handleApplyFilters}>
                                            Buscar
                                        </Button>
                                        <Button onClick={handleClearFilters}>
                                            Limpiar
                                        </Button>
                                    </Space>
                                </div>

                                {/* Table */}
                                <div className="clients-table-container">
                                    <Table
                                        columns={columns}
                                        dataSource={clients}
                                        rowKey="id"
                                        loading={loading}
                                        pagination={pagination}
                                        onChange={handleTableChange}
                                        scroll={{ x: 1400 }}
                                        size="middle"
                                    />
                                </div>

                                {/* Client Creation Modal */}
                                <ClientCreationModal
                                    visible={modalVisible}
                                    onClose={() => setModalVisible(false)}
                                    onClientCreated={handleClientCreated}
                                />

                                {/* Client Edit Modal */}
                                <ClientEditModal
                                    visible={editModalVisible}
                                    onClose={() => {
                                        setEditModalVisible(false);
                                        setSelectedClient(null);
                                    }}
                                    onClientUpdated={handleClientUpdated}
                                    client={selectedClient}
                                />

                                {/* Running Account Enrollment Modal */}
                                <AgregarClienteRunningAccountModal
                                    visible={runningAccountModalVisible}
                                    onClose={() => {
                                        setRunningAccountModalVisible(false);
                                        setSelectedClient(null);
                                    }}
                                    onClientCreated={handleClientAddedToRunningAccount}
                                    prefilledClient={selectedClient}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'cuentas',
                        label: (
                            <span>
                                <WalletOutlined style={{ marginRight: 8 }} />
                                Cuentas Corrientes
                            </span>
                        ),
                        children: <CuentasCorrientesTab />,
                    },
                    {
                        key: 'cuentas-abiertas',
                        label: (
                            <span>
                                <ContainerOutlined style={{ marginRight: 8 }} />
                                Cuentas Abiertas
                            </span>
                        ),
                        children: <CuentasAbiertasTab />,
                    },
                ]}
            />
        </div>
    );
};

export default ClientsPage;
